import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../lib/LanguageContext';
import { translations } from '../lib/translations';
export function BreakthroughSection() {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const {
    lang
  } = useLanguage();
  const t = translations[lang].breakthrough;
  return <section ref={ref} style={{
    backgroundColor: 'var(--white)',
    padding: '10rem 0',
    position: 'relative',
    overflow: 'hidden'
  }} data-id="element-16">
      <div style={{
      maxWidth: '48rem',
      margin: '0 auto',
      padding: '0 2rem'
    }} data-id="element-17">
        {/* Section label */}
        <motion.p initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {
        opacity: 0
      }} transition={{
        duration: 0.6
      }} style={{
        fontFamily: '"IBM Plex Mono", monospace',
        fontSize: '0.75rem',
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
        color: 'var(--grey-500)',
        marginBottom: '4rem'
      }} data-id="element-18">
          {t.label}
        </motion.p>

        {/* Title */}
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.7,
        delay: 0.1
      }} style={{
        fontFamily: '"IBM Plex Sans", sans-serif',
        fontWeight: 900,
        fontSize: 'clamp(2.5rem, 5vw, 3.75rem)',
        lineHeight: 1.1,
        color: 'var(--black)',
        marginBottom: '1.5rem'
      }} data-id="element-19">
          {t.title}
        </motion.h2>

        {/* Intro */}
        <motion.p initial={{
        opacity: 0,
        y: 16
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} transition={{
        duration: 0.7,
        delay: 0.2
      }} style={{
        fontFamily: '"IBM Plex Sans", sans-serif',
        fontWeight: 300,
        fontSize: 'clamp(1rem, 2vw, 1.25rem)',
        color: 'var(--grey-700)',
        marginBottom: '4rem',
        lineHeight: 1.7
      }} data-id="element-20">
          {t.intro}
        </motion.p>

        {/* Computed facts list */}
        <div style={{
        marginBottom: '4rem'
      }} data-id="element-21">
          {t.facts.map((fact, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -16
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {
          opacity: 0,
          x: -16
        }} transition={{
          duration: 0.6,
          delay: 0.3 + i * 0.1
        }} style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: '1rem',
          marginBottom: '1rem'
        }} data-id="element-22">
              <span style={{
            color: 'var(--red)',
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.875rem',
            flexShrink: 0,
            lineHeight: 1,
            paddingTop: '0.1rem'
          }} data-id="element-23">
                —
              </span>
              <p style={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 300,
            fontSize: 'clamp(1rem, 2vw, 1.25rem)',
            color: 'var(--black)',
            margin: 0,
            lineHeight: 1.7
          }} data-id="element-24">
                {fact.prefix}
                <strong style={{
              fontWeight: 700
            }} data-id="element-25">
                  {fact.emphasis}
                </strong>
              </p>
            </motion.div>)}
        </div>

        {/* Divider */}
        <motion.hr initial={{
        opacity: 0,
        scaleX: 0
      }} animate={isInView ? {
        opacity: 1,
        scaleX: 1
      } : {
        opacity: 0,
        scaleX: 0
      }} transition={{
        duration: 0.6,
        delay: 0.85
      }} style={{
        border: 'none',
        borderTop: '1px solid var(--grey-300)',
        margin: '4rem 0',
        transformOrigin: 'left'
      }} data-id="element-26" />

        {/* Closing lines */}
        <motion.p initial={{
        opacity: 0,
        y: 16
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} transition={{
        duration: 0.7,
        delay: 0.95
      }} style={{
        fontFamily: '"IBM Plex Sans", sans-serif',
        fontWeight: 300,
        fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
        color: 'var(--grey-700)',
        marginBottom: '0.5rem'
      }} data-id="element-27">
          {t.closing1}
        </motion.p>

        <motion.p initial={{
        opacity: 0,
        y: 16
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} transition={{
        duration: 0.7,
        delay: 1.05
      }} style={{
        fontFamily: '"IBM Plex Sans", sans-serif',
        fontWeight: 900,
        fontSize: 'clamp(1.5rem, 3vw, 2rem)',
        color: 'var(--black)',
        marginBottom: 0
      }} data-id="element-28">
          {t.closing2}
        </motion.p>

        {/* Watermark word */}
        <motion.div initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {
        opacity: 0
      }} transition={{
        duration: 1.2,
        delay: 1.15
      }} style={{
        fontFamily: '"IBM Plex Sans", sans-serif',
        fontWeight: 900,
        fontSize: 'clamp(80px, 12vw, 180px)',
        color: 'var(--grey-100)',
        lineHeight: 1,
        marginTop: '2rem',
        letterSpacing: '-0.02em',
        userSelect: 'none'
      }} data-id="element-29">
          {t.watermark}
        </motion.div>
      </div>
    </section>;
}